
import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, Users, Building2, Search } from 'lucide-react';
import { Client } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  client?: Client | null;
  onSave: (client: Client) => Promise<void>;
}

const ClientModal: React.FC<Props> = ({ isOpen, onClose, client, onSave }) => {
  const [formData, setFormData] = useState<Partial<Client>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (client) setFormData({ ...client });
      else setFormData({ client_code: '', client_name: '', client_kana: '', client_abbreviation: '', imported_name: '', densai_user_code: '', notes: '', balance: 0, is_union: false });
    }
  }, [isOpen, client]);

  // コード変更時に自動判定（手動変更が優先されるべき場合はこのロジックを調整）
  useEffect(() => {
      const code = formData.client_code || '';
      if (!client) { // 新規登録時のみ自動補完
          if (code.startsWith('83')) {
              setFormData(prev => ({...prev, is_union: true, union_type: 'minamisatsuma'}));
          } else if (code.startsWith('88')) {
              setFormData(prev => ({...prev, is_union: true, union_type: 'satsuma'}));
          }
      }
  }, [formData.client_code, client]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
        await onSave(formData as Client);
        onClose();
    } catch (e) {
        console.error(e);
    } finally {
        setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in duration-200">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24}/></button>
        <h2 className="text-xl font-bold mb-6 text-gray-800">{client ? '得意先情報編集' : '新規得意先登録'}</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">得意先CD <span className="text-red-500">*</span></label>
                    <input 
                        type="text" 
                        value={formData.client_code||''} 
                        onChange={e => setFormData({...formData, client_code: e.target.value})} 
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none" 
                        required 
                    />
                </div>
                
                <div className="bg-gray-50 p-2 rounded border border-gray-200">
                    <label className="flex items-center gap-2 mb-2 cursor-pointer">
                        <input 
                            type="checkbox" 
                            checked={!!formData.is_union}
                            onChange={e => {
                                const checked = e.target.checked;
                                setFormData(prev => ({
                                    ...prev, 
                                    is_union: checked,
                                    // チェックON時、union_typeが未設定ならデフォルト'satsuma'をセット
                                    union_type: checked && !prev.union_type ? 'satsuma' : prev.union_type
                                }));
                            }}
                            className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm font-bold text-gray-700 flex items-center gap-1"><Users size={14}/> 組合所属</span>
                    </label>
                    
                    {formData.is_union && (
                        <select 
                            value={formData.union_type || 'satsuma'} 
                            onChange={e => setFormData({...formData, union_type: e.target.value as any})}
                            className="w-full text-xs border rounded p-1.5 font-bold"
                        >
                            <option value="satsuma">薩摩砕石協同組合</option>
                            <option value="minamisatsuma">南さつまリサイクル</option>
                        </select>
                    )}
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">得意先名 <span className="text-red-500">*</span></label>
                <input 
                    type="text" 
                    value={formData.client_name||''} 
                    onChange={e => setFormData({...formData, client_name: e.target.value})} 
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none" 
                    required 
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Search size={14} className="text-gray-400"/> 照合用キーワード (取込名称)
                </label>
                <input 
                    type="text" 
                    value={formData.imported_name||''} 
                    onChange={e => setFormData({...formData, imported_name: e.target.value})} 
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none" 
                    placeholder="例：ユルリカ（入金専用）"
                />
                <p className="text-[11px] text-gray-500 mt-1 ml-1">
                    ※組合明細(PDF)や振込データ上の名称が、マスタの名称と大きく異なる場合に登録します。ここで設定した名称と一致した場合、この得意先として扱われます。
                </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">略称</label>
                    <input 
                        type="text" 
                        value={formData.client_abbreviation||''} 
                        onChange={e => setFormData({...formData, client_abbreviation: e.target.value})} 
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none" 
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">カナ</label>
                    <input 
                        type="text" 
                        value={formData.client_kana||''} 
                        onChange={e => setFormData({...formData, client_kana: e.target.value})} 
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none" 
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">でんさい利用者番号</label>
                <input 
                    type="text" 
                    value={formData.densai_user_code||''} 
                    onChange={e => setFormData({...formData, densai_user_code: e.target.value})} 
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono placeholder:text-gray-300"
                    placeholder="例：00008CPV6" 
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">備考</label>
                <textarea 
                    value={formData.notes||''} 
                    onChange={e => setFormData({...formData, notes: e.target.value})} 
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none h-20" 
                />
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 rounded text-gray-700 hover:bg-gray-50 transition">キャンセル</button>
                <button type="submit" disabled={isSaving} className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 font-bold transition flex items-center gap-2">
                    <Save size={18}/> 保存
                </button>
            </div>
        </form>
      </div>
    </div>
  );
};
export default ClientModal;
